import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { StatsPeopleList } from "@/components/stats/StatsPeopleList";
import { api } from "@/lib/api";
import { callPeriodRange, type CallPeriodKey } from "@/lib/call-periods";
import { TONE_STYLES } from "@/lib/colors";
import { TODAY_CHANGED_EVENT } from "@/lib/pointage";
import { cn } from "@/lib/utils";
import { parisDateOf } from "@shared/followup";
import type { CallStatsBlock } from "@shared/call-stats";
import type { Prospect } from "@shared/schema";

const STORAGE_KEY = "crm.callsPeriod";
const KEYS: CallPeriodKey[] = ["week", "lastWeek", "month", "lastMonth"];

function loadKey(): CallPeriodKey {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && (KEYS as string[]).includes(v)) return v as CallPeriodKey;
  } catch {
    /* ignore */
  }
  return "week";
}

const pct = (r: number | null) => (r === null ? "–" : `${Math.round(r * 100)} %`);

function Num({
  value,
  label,
  tone,
  onClick,
  active,
}: {
  value: number;
  label: React.ReactNode;
  tone?: "green" | "slate" | "coral";
  onClick?: () => void;
  active?: boolean;
}) {
  const color =
    tone === "green"
      ? "text-[var(--color-success)]"
      : tone === "slate"
        ? "text-[#475569]"
        : tone === "coral"
          ? "text-[var(--color-coral-600)]"
          : "text-[var(--color-navy-700)]";
  const inner = (
    <>
      <div className={cn("tabular text-[26px] font-extrabold leading-none", color)}>{value}</div>
      <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{label}</div>
    </>
  );
  if (!onClick) return <div className="min-w-[70px]">{inner}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={active}
      className={cn(
        "-m-1 min-w-[70px] rounded-lg p-1 text-left hover:bg-[var(--color-muted)]",
        active && "bg-[var(--color-muted)]",
      )}
    >
      {inner}
    </button>
  );
}

/** Bandeau tracker : RDV, présents, absents, à pointer, closés sur une période. */
export function CallsStrip({
  bookingUrl,
  onPointed,
}: {
  bookingUrl: string;
  onPointed?: (eventId: number, prospect: Prospect, outcome: "attended" | "no_show") => void;
}) {
  const [key, setKey] = useState<CallPeriodKey>(loadKey);
  const [block, setBlock] = useState<CallStatsBlock | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<"absents" | "aPointer" | null>(null);
  const reqId = useRef(0);

  const today = parisDateOf(new Date().toISOString()) ?? "";
  const range = callPeriodRange(key, today);
  const { from, to } = range;

  const load = useCallback(() => {
    const id = ++reqId.current;
    setFailed(false);
    api
      .callStats({ from, to })
      .then((r) => {
        if (id === reqId.current) setBlock(r.total);
      })
      .catch(() => {
        if (id === reqId.current) setFailed(true);
      });
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.addEventListener(TODAY_CHANGED_EVENT, load);
    return () => window.removeEventListener(TODAY_CHANGED_EVENT, load);
  }, [load]);

  // Le dépliant se referme quand sa liste se vide (dernier pointage fait, chiffres rechargés).
  const openCount = open && block ? block.counts[open] : null;
  useEffect(() => {
    if (openCount === 0) setOpen(null);
  }, [openCount]);

  function choose(k: CallPeriodKey) {
    if (k === key) return;
    setKey(k);
    setOpen(null);
    setBlock(null);
    try {
      localStorage.setItem(STORAGE_KEY, k);
    } catch {
      /* ignore */
    }
  }

  const c = block?.counts;
  const total = c?.rdv ?? 0;
  const w = (n: number) => (total > 0 ? `${(n / total) * 100}%` : "0%");
  const toggle = (k: "absents" | "aPointer") => setOpen((o) => (o === k ? null : k));

  return (
    <section className="surface mb-4 px-4 py-3.5" aria-label="Présents et absents">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2.5">
        <div className="-mx-1 max-w-full overflow-x-auto px-1">
          <div className="inline-flex overflow-hidden rounded-[9px] border border-[var(--color-border-strong)] bg-[var(--color-surface)]">
            {KEYS.map((k) => {
              const label = callPeriodRange(k, today).label;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => choose(k)}
                  aria-pressed={k === key}
                  className={cn(
                    "whitespace-nowrap px-3 py-1.5 text-[13px] font-semibold max-sm:min-h-11",
                    k === key
                      ? "bg-[var(--color-navy-700)] text-white"
                      : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <Link
          to={`/statistiques?mois=${range.month}`}
          className="text-xs font-semibold text-[var(--color-navy-700)] hover:underline max-sm:hidden"
        >
          Voir le détail
        </Link>
      </div>

      {failed && !block ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Chiffres indisponibles ·{" "}
          <button type="button" className="font-semibold underline" onClick={load}>
            Réessayer
          </button>
        </p>
      ) : !block || !c ? (
        <div className="h-[60px] animate-pulse rounded-lg bg-[var(--color-muted)]" aria-busy="true" />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3 sm:gap-x-[22px]">
            <Num value={c.rdv} label="RDV" />
            <Num value={c.presents} tone="green" label={`présents · ${pct(c.presenceRate)}`} />
            <Num
              value={c.absents}
              tone="slate"
              label={<span className="underline decoration-dotted">absents {open === "absents" ? "▴" : "▾"}</span>}
              onClick={() => toggle("absents")}
              active={open === "absents"}
            />
            {c.aPointer > 0 && (
              <Num
                value={c.aPointer}
                tone="coral"
                label={<span className="underline decoration-dotted">à pointer {open === "aPointer" ? "▴" : "▾"}</span>}
                onClick={() => toggle("aPointer")}
                active={open === "aPointer"}
              />
            )}
            <Num value={c.closes} label={`closés · ${pct(c.closingRate)} des présents`} />
          </div>

          <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-[var(--color-muted)]">
            <i style={{ width: w(c.presents) }} className="block h-full bg-[var(--color-success)]" />
            <i style={{ width: w(c.absents) }} className={cn("block h-full", TONE_STYLES.slate.dot)} />
            <i style={{ width: w(c.aPointer) }} className="block h-full bg-[var(--color-coral-500)]" />
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-3.5 text-[11px] text-[var(--color-muted-foreground)]">
            {[
              ["Présents", "bg-[var(--color-success)]"],
              ["Absents", TONE_STYLES.slate.dot],
              ["À pointer", "bg-[var(--color-coral-500)]"],
            ].map(([l, bg]) => (
              <span key={l} className="inline-flex items-center gap-1.5">
                <span className={cn("inline-block h-[9px] w-[9px] rounded-sm", bg)} />
                {l}
              </span>
            ))}
          </div>

          {open && (
            <div className="mt-3 border-t border-[var(--color-border)] pt-2.5">
              {open === "absents" ? (
                <>
                  <p className="mb-1 text-xs text-[var(--color-muted-foreground)]">
                    {c.absents} absents · {c.absentsRepris} ont repris RDV ·{" "}
                    {c.absents - c.absentsRepris} à recontacter
                  </p>
                  <StatsPeopleList kind="absents" entries={block.absents} bookingUrl={bookingUrl} />
                </>
              ) : (
                <StatsPeopleList
                  kind="aPointer"
                  entries={block.aPointer}
                  bookingUrl={bookingUrl}
                  onPointed={onPointed}
                />
              )}
            </div>
          )}
        </>
      )}
      <Link
        to={`/statistiques?mois=${range.month}`}
        className="-mb-1.5 mt-1.5 hidden min-h-11 items-center text-xs font-semibold text-[var(--color-navy-700)] hover:underline max-sm:inline-flex"
      >
        Voir le détail
      </Link>
    </section>
  );
}
